import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { signatureRequests, type SignatureRequest } from '@/shared/api/resources';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { KpiCard } from '@/shared/ui/KpiCard';
import { Modal } from '@/shared/ui/Modal';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import {
  SignatureRequestDetail,
  SignatureRequestStatusBadge,
} from './SignatureRequestBlocks';
import { situacaoDoPedido } from './signatureRequestLabels';

/**
 * Aba "Pedidos ao provedor" de /assinaturas (P-MIN-4): os pedidos de assinatura
 * eletronica, com o provedor, o prazo, os signatarios e o log de cada um. Ver
 * docs/ADR/0062-assinatura-por-provedor.md.
 */

const limite = 25;

export function SignatureRequestsTab() {
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [busca, setBusca] = useState('');
  const [offset, setOffset] = useState(0);
  const [aberto, setAberto] = useState<SignatureRequest | null>(null);

  const consulta = useQuery({
    queryKey: ['signature-requests', 'lista', { status, provider, busca, offset }],
    queryFn: ({ signal }) =>
      signatureRequests.list(
        { status, provider, q: busca, limit: limite, offset, sort: '-created_at' },
        signal,
      ),
  });
  const contagem = consulta.data?.counts ?? {};

  const colunas: Column<SignatureRequest>[] = [
    {
      key: 'pedido',
      header: 'Pedido',
      render: (p) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">
            {p.number} · {p.subject_label || 'Documento'}
          </p>
          <p className="truncate text-2xs text-ink-500">
            {p.trigger === 'automatico' ? 'Pedido automatico' : p.requested_label}
          </p>
        </div>
      ),
    },
    {
      key: 'provedor',
      header: 'Provedor',
      hideBelow: 'md',
      render: (p) => p.provider_name,
    },
    {
      key: 'signatarios',
      header: 'Signatarios',
      hideBelow: 'md',
      render: (p) =>
        `${p.signers.filter((s) => s.status === 'assinado').length} de ${p.signers.length} assinaram`,
    },
    {
      key: 'prazo',
      header: 'Prazo',
      align: 'right',
      hideBelow: 'lg',
      render: (p) => (
        <span className="inline-flex items-center gap-1">
          {formatDate(p.deadline_at)}
          {p.overdue ? <Badge tone="warning">Vencido</Badge> : null}
        </span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (p) => <SignatureRequestStatusBadge pedido={p} />,
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (p) => (
        <Button variant="secondary" onClick={() => setAberto(p)}>
          Abrir
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os pedidos">
          {consulta.error.message}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-4">
        <KpiCard
          label="Aguardando assinatura"
          value={String(contagem.pendente ?? 0)}
          icon="signature"
          tone="warning"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Assinados"
          value={String(contagem.assinado ?? 0)}
          icon="check"
          tone="success"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Recusados ou vencidos"
          value={String((contagem.recusado ?? 0) + (contagem.expirado ?? 0))}
          icon="close"
          tone="danger"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Erro no envio"
          value={String(contagem.erro ?? 0)}
          icon="alert"
          tone="danger"
          highlight={(contagem.erro ?? 0) > 0}
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Situacao do pedido"
            className="h-9"
            options={[
              { value: '', label: 'Todas as situacoes' },
              ...Object.entries(situacaoDoPedido).map(([value, estado]) => ({
                value,
                label: estado.label,
              })),
            ]}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
          <Select
            label="Provedor de assinatura"
            className="h-9"
            options={[
              { value: '', label: 'Todos os provedores' },
              { value: 'clicksign', label: 'Clicksign' },
              { value: 'falso', label: 'Provedor falso' },
            ]}
            value={provider}
            onChange={(event) => {
              setProvider(event.target.value);
              setOffset(0);
            }}
          />
          <Field
            label="Buscar pedido"
            placeholder="Numero, documento ou signatario"
            value={busca}
            onChange={(event) => {
              setBusca(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(p) => p.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="signature"
              title="Nenhum pedido ao provedor"
              description="O pedido nasce no documento: no detalhe da apolice ou no quadro da minuta e da CCG, com o botao Pedir assinatura."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {aberto ? (
        <Modal
          open
          onClose={() => setAberto(null)}
          title={`Pedido de assinatura ${aberto.number}`}
          description={aberto.subject_label}
          size="lg"
          footer={<Button onClick={() => setAberto(null)}>Fechar</Button>}
        >
          <SignatureRequestDetail requestId={aberto.id} />
        </Modal>
      ) : null}
    </div>
  );
}
